import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  InputAdornment,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ResponsiveDialog from '../components/ResponsiveDialog';
import ResponsiveTableContainer from '../components/ResponsiveTableContainer';
import {
  Search as SearchIcon,
  CompareArrows as CompareArrowsIcon,
  Autorenew as AutorenewIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';

type MatchRow = {
  id: number;
  status: string;
  match_score: string | null;
  match_reason: string | null;
  created_at: string | null;
  source_announcement: { id: number; title: string; type: string; user_email: string | null } | null;
  target_announcement: { id: number; title: string; type: string; user_email: string | null } | null;
  target_found_object: { id: number; title: string; reference_code: string | null } | null;
};

type SourceGroup = {
  key: string;
  source: MatchRow['source_announcement'];
  matches: MatchRow[];
  bestScore: number | null;
};

function scoreNumber(score: string | null): number | null {
  if (score == null || score === '') return null;
  const n = Number(score);
  return Number.isFinite(n) ? n : null;
}

function groupMatchesBySource(rows: MatchRow[]): SourceGroup[] {
  const map = new Map<string, SourceGroup>();

  for (const row of rows) {
    const sourceId = row.source_announcement?.id;
    const key = sourceId != null ? `ann-${sourceId}` : `orphan-${row.id}`;
    let group = map.get(key);
    if (!group) {
      group = {
        key,
        source: row.source_announcement,
        matches: [],
        bestScore: null,
      };
      map.set(key, group);
    }
    group.matches.push(row);
    const s = scoreNumber(row.match_score);
    if (s != null && (group.bestScore == null || s > group.bestScore)) {
      group.bestScore = s;
    }
  }

  const groups = Array.from(map.values());
  for (let i = 0; i < groups.length; i++) {
    groups[i].matches.sort((a, b) => (scoreNumber(b.match_score) ?? -1) - (scoreNumber(a.match_score) ?? -1));
  }

  return groups.sort((a, b) => {
    const aId = a.source?.id ?? 0;
    const bId = b.source?.id ?? 0;
    if (a.matches.length !== b.matches.length) {
      return b.matches.length - a.matches.length;
    }
    return bId - aId;
  });
}

const MatchesAdminPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedMatchId, setSelectedMatchId] = useState<number | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-matchings', search, page, rowsPerPage],
    queryFn: () =>
      apiService.getAdminMatchings({
        search: search || undefined,
        page: page + 1,
        limit: rowsPerPage,
      }),
  });

  const recalcMutation = useMutation({
    mutationFn: () => apiService.postAdminMatchingsRecalculate(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-matchings'] });
    },
  });

  const detailQuery = useQuery({
    queryKey: ['admin-matching', selectedMatchId],
    queryFn: () => apiService.getAdminMatching(selectedMatchId as number),
    enabled: selectedMatchId != null,
  });

  const forceEmailMutation = useMutation({
    mutationFn: (id: number) => apiService.postAdminMatchingForceEmail(id),
    onSuccess: () => {
      if (selectedMatchId != null) {
        queryClient.invalidateQueries({ queryKey: ['admin-matching', selectedMatchId] });
      }
    },
  });

  const rows: MatchRow[] = useMemo(() => data?.data ?? [], [data]);
  const groups = useMemo(() => groupMatchesBySource(rows), [rows]);

  useEffect(() => {
    // Ouvre le premier groupe de la page (souvent celui avec le plus de cibles).
    setExpandedKeys(groups.length > 0 ? [groups[0].key] : []);
  }, [groups]);

  const handleSearch = () => {
    setSearch(searchInput.trim());
    setPage(0);
  };

  const toggleExpanded = (key: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedKeys((prev) => {
      if (isExpanded) {
        return prev.includes(key) ? prev : [...prev, key];
      }
      return prev.filter((k) => k !== key);
    });
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <CompareArrowsIcon color="primary" />
        <Typography variant="h4" component="h1">
          Matchings
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
        Correspondances regroupées par annonce source. Le recalcul complet s’exécute après la réponse HTTP ;
        les e-mails utilisent <code>MAIL_SEND_GAP_MICROS</code> côté API. Cron :{' '}
        <code>0 0 * * * php bin/console app:matches:generate</code>
      </Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
            <TextField
              size="small"
              label="Recherche"
              placeholder="ID, titre, e-mail, référence objet…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              sx={{ minWidth: 280, flex: 1 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <Button variant="outlined" onClick={handleSearch}>
              Rechercher
            </Button>
            <Box flexGrow={1} />
            <Button
              variant="contained"
              color="primary"
              startIcon={recalcMutation.isPending ? <CircularProgress size={18} color="inherit" /> : <AutorenewIcon />}
              disabled={recalcMutation.isPending}
              onClick={() => recalcMutation.mutate()}
            >
              Recalculer les matchings
            </Button>
          </Box>
          {recalcMutation.isSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {recalcMutation.data?.message}
            </Alert>
          )}
          {recalcMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Impossible de lancer le recalcul (droits super-admin ou session expirée).
            </Alert>
          )}
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Erreur lors du chargement des matchings.
        </Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper variant="outlined">
          {groups.length === 0 ? (
            <Typography color="text.secondary" align="center" py={4}>
              Aucun matching pour ces critères.
            </Typography>
          ) : (
            <Box sx={{ px: { xs: 0.5, sm: 1 }, py: 1 }}>
              {groups.map((group) => {
                const source = group.source;
                return (
                  <Accordion
                    key={group.key}
                    disableGutters
                    elevation={0}
                    expanded={expandedKeys.includes(group.key)}
                    onChange={toggleExpanded(group.key)}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      mb: 1,
                      borderRadius: 1,
                      '&:before': { display: 'none' },
                      overflow: 'hidden',
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        px: { xs: 1.5, sm: 2 },
                        '& .MuiAccordionSummary-content': {
                          my: 1.25,
                          alignItems: 'center',
                          gap: 1,
                          flexWrap: 'wrap',
                        },
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                          {source?.title ?? 'Annonce source inconnue'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {source
                            ? `#${source.id} · ${source.type}${source.user_email ? ` · ${source.user_email}` : ''}`
                            : '—'}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Chip
                          size="small"
                          color="primary"
                          variant="outlined"
                          label={`${group.matches.length} matching${group.matches.length > 1 ? 's' : ''}`}
                        />
                        {group.bestScore != null && (
                          <Chip
                            size="small"
                            label={`Meilleur ${group.bestScore.toFixed(2)}`}
                            sx={{ fontWeight: 600 }}
                          />
                        )}
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0, pb: 0, pt: 0 }}>
                      <ResponsiveTableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>ID</TableCell>
                              <TableCell>Statut</TableCell>
                              <TableCell>Score</TableCell>
                              <TableCell>Cible</TableCell>
                              <TableCell>Créé le</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {group.matches.map((r) => (
                              <TableRow
                                key={r.id}
                                hover
                                sx={{ cursor: 'pointer' }}
                                onClick={() => setSelectedMatchId(r.id)}
                              >
                                <TableCell>{r.id}</TableCell>
                                <TableCell>
                                  <Chip label={r.status} size="small" variant="outlined" />
                                </TableCell>
                                <TableCell>{r.match_score ?? '—'}</TableCell>
                                <TableCell>
                                  {r.target_announcement ? (
                                    <>
                                      <Typography variant="body2">{r.target_announcement.title}</Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        #{r.target_announcement.id} · {r.target_announcement.type}
                                        {r.target_announcement.user_email
                                          ? ` · ${r.target_announcement.user_email}`
                                          : ''}
                                      </Typography>
                                    </>
                                  ) : r.target_found_object ? (
                                    <>
                                      <Typography variant="body2">
                                        Objet trouvé : {r.target_found_object.title}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        #{r.target_found_object.id}
                                        {r.target_found_object.reference_code
                                          ? ` · ${r.target_found_object.reference_code}`
                                          : ''}
                                      </Typography>
                                    </>
                                  ) : (
                                    '—'
                                  )}
                                </TableCell>
                                <TableCell>{r.created_at ?? '—'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </ResponsiveTableContainer>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          )}
          <TablePagination
            component="div"
            count={data?.total ?? 0}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Par page"
          />
        </Paper>
      )}

      <ResponsiveDialog open={selectedMatchId != null} onClose={() => setSelectedMatchId(null)} maxWidth="md" fullWidth>
        <DialogTitle>Détail du matching</DialogTitle>
        <DialogContent dividers>
          {detailQuery.isLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={24} />
            </Box>
          ) : detailQuery.isError ? (
            <Alert severity="error">Impossible de charger le détail du matching.</Alert>
          ) : detailQuery.data?.data ? (
            <Stack spacing={2}>
              <Typography variant="body2">
                <strong>ID :</strong> {detailQuery.data.data.id}
              </Typography>
              <Typography variant="body2">
                <strong>Statut :</strong> {detailQuery.data.data.status}
              </Typography>
              <Typography variant="body2">
                <strong>Score :</strong> {detailQuery.data.data.match_score ?? '—'}
              </Typography>
              <Typography variant="body2">
                <strong>Créé le :</strong> {detailQuery.data.data.created_at ?? '—'}
              </Typography>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Source
                </Typography>
                <Typography variant="body2">
                  {detailQuery.data.data.source_announcement
                    ? `${detailQuery.data.data.source_announcement.title} (#${detailQuery.data.data.source_announcement.id})`
                    : '—'}
                </Typography>
                {detailQuery.data.data.source_announcement?.user_email && (
                  <Typography variant="caption" color="text.secondary">
                    {detailQuery.data.data.source_announcement.user_email}
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Cible
                </Typography>
                {detailQuery.data.data.target_announcement ? (
                  <>
                    <Typography variant="body2">
                      {detailQuery.data.data.target_announcement.title} (#{detailQuery.data.data.target_announcement.id})
                    </Typography>
                    {detailQuery.data.data.target_announcement.user_email && (
                      <Typography variant="caption" color="text.secondary">
                        {detailQuery.data.data.target_announcement.user_email}
                      </Typography>
                    )}
                  </>
                ) : detailQuery.data.data.target_found_object ? (
                  <>
                    <Typography variant="body2">
                      Objet trouvé : {detailQuery.data.data.target_found_object.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      #{detailQuery.data.data.target_found_object.id}
                      {detailQuery.data.data.target_found_object.reference_code
                        ? ` · ${detailQuery.data.data.target_found_object.reference_code}`
                        : ''}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2">—</Typography>
                )}
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Détails du matching
                </Typography>
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    {(detailQuery.data.data.match_reason ?? 'Aucun détail').replace(/;\s/g, '\n')}
                  </Typography>
                </Paper>
              </Box>

              {forceEmailMutation.isSuccess && (
                <Alert severity="success">
                  {forceEmailMutation.data.message}
                  {forceEmailMutation.data.sent_to.length > 0 && (
                    <> Destinataires: {forceEmailMutation.data.sent_to.join(', ')}</>
                  )}
                </Alert>
              )}
              {forceEmailMutation.isError && (
                <Alert severity="error">Impossible de forcer l’envoi des e-mails pour ce matching.</Alert>
              )}
            </Stack>
          ) : (
            <Alert severity="warning">Matching introuvable.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedMatchId(null)}>Fermer</Button>
          <Button
            variant="contained"
            onClick={() => selectedMatchId != null && forceEmailMutation.mutate(selectedMatchId)}
            disabled={selectedMatchId == null || forceEmailMutation.isPending}
          >
            {forceEmailMutation.isPending ? 'Envoi...' : 'Forcer envoi e-mail'}
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </Box>
  );
};

export default MatchesAdminPage;
